import React from 'react';

import { observer } from 'mobx-react';
import { reverse } from 'named-urls';
import { AxiosError } from 'axios';

import ActionCell, { ActionConfig } from 'components/Grid/components/ActionCell';
import { ColumnActions } from 'components/Grid/types';
import routes from 'core/routes';
import { useStore } from 'utils/hooks/store';
import { ScheduleDetailsResponse } from 'utils/api/modules/schedules/schedules.types';

interface Props {
  rowData: ScheduleDetailsResponse;
  actions: ColumnActions;
}

const SchedulesActionCell: React.FC<Props> = ({ rowData, actions }) => {
  const { scheduleDetails, routing, notification } = useStore();

  const handleDelete = async (): Promise<void> => {
    try {
      await scheduleDetails.deleteSchedule(rowData.uuid);
      notification.enqueueSuccessSnackbar('Schedule deleted successfully');
      await actions.refreshData();
    } catch (e) {
      const error = e as AxiosError<string[]>;
      notification.enqueueErrorSnackbar(error.response?.data?.[0] || 'Failed to delete schedule');
    }
  };

  const handlePause = async (): Promise<void> => {
    const ok = await scheduleDetails.pauseSchedule(rowData.uuid);
    if (ok) {
      await actions.refreshData();
    }
  };

  const actionConfigs: ActionConfig[] = [
    {
      id: 'edit-button',
      label: 'Edit',
      onClick: () => {
        routing.push(reverse(routes.dashboard.schedules.details, { scheduleId: rowData.uuid }));
      },
    },
    ...(rowData.isPaused
      ? []
      : [
          {
            id: 'pause-button',
            label: 'Pause',
            onClick: handlePause,
            confirmation: {
              type: 'simple' as const,
              title: 'Pause Schedule',
              message: `Pausing ${rowData.name} will stop upcoming instances from being created. You can resume at any time.`,
              confirmButtonLabel: 'Pause',
              cancelButtonLabel: 'Cancel',
              hideRecoveryWarning: true,
            },
          },
        ]),
    {
      id: 'remove-button',
      label: 'Delete',
      onClick: handleDelete,
      confirmation: {
        type: 'text-input',
        title: 'Delete Schedule',
        message: 'Are you sure you want to delete this schedule?',
      },
    },
  ];

  return <ActionCell actions={actionConfigs} recordName={rowData.name} />;
};

export default observer(SchedulesActionCell);
